import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Landmark, Loader2, CheckCircle2, XCircle, AlertTriangle, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function Check({ ok, label }) {
  return (
    <li className="flex items-center gap-2 text-sm" data-testid={`escrow-check-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      {ok ? <CheckCircle2 size={15} className="text-emerald-600" /> : <XCircle size={15} className="text-slate-300" />}
      <span className={ok ? "text-slate-700" : "text-slate-400"}>{label}</span>
    </li>
  );
}

export default function EscrowPanel({ deal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/deals/${deal.deal_id}/escrow`);
      setData(d);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [deal.deal_id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="mt-6 py-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

  const esc = data?.escrow;
  const fr = data?.funding_readiness || { ready: false, unmet: [] };
  const rr = data?.release_readiness || { ready: false, unmet: [] };
  const holds = (deal.holds || []).filter((h) => h.active);
  const unmet = new Set([...(fr.unmet || []), ...(rr.unmet || [])]);
  const has = (k) => !unmet.has(k);

  return (
    <div className="mt-6 border-t border-slate-200 pt-5 space-y-5" data-testid="escrow-panel">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Landmark size={15} /> Escrow readiness</h4>

      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-amber-800" data-testid="escrow-simulated-banner">
        SIMULATED — NO LIVE MONEY MOVEMENT
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Provider & status</div>
          <div className="rounded-md border border-slate-200 p-3 text-sm" data-testid="escrow-status">
            <div>Provider status: <span className="font-medium">{esc?.provider_status || esc?.status || "not onboarded"}</span></div>
            <div className="mt-1">Escrow state: <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{esc?.status || "none"}</span></div>
          </div>
        </div>
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Approvals</div>
          <ul className="rounded-md border border-slate-200 p-3">
            <Check ok={!!esc?.compliance_approved} label="Compliance approval" />
            <Check ok={(esc?.release_approvals || []).length >= 1} label="First finance approval" />
            <Check ok={(esc?.release_approvals || []).length >= 2} label="Dual approval complete" />
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase text-slate-400">Funding readiness
            {fr.ready ? <span className="rounded bg-emerald-100 px-1.5 text-emerald-700">Ready</span> : <span className="rounded bg-amber-100 px-1.5 text-amber-800">Not ready</span>}</div>
          <ul className="rounded-md border border-slate-200 p-3" data-testid="escrow-funding-checklist">
            <Check ok={has("business_verification")} label="Parties verified" />
            <Check ok={has("compliance_approval")} label="Compliance approval" />
            <Check ok={has("contract_executed")} label="Contract executed" />
            <Check ok={has("no_active_holds") && holds.length === 0} label="No active blocking holds" />
          </ul>
        </div>
        <div>
          <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase text-slate-400">Release conditions
            {rr.ready ? <span className="rounded bg-emerald-100 px-1.5 text-emerald-700">Ready</span> : <span className="rounded bg-red-100 px-1.5 text-red-700">Blocked</span>}</div>
          <ul className="rounded-md border border-slate-200 p-3" data-testid="escrow-release-checklist">
            <Check ok={has("funds_secured")} label="Funds secured" />
            <Check ok={has("delivery_accepted") && has("required_evidence")} label="Delivery accepted + evidence" />
            <Check ok={has("no_active_dispute")} label="No active dispute" />
            <Check ok={has("dual_release_approval")} label="Required approvals complete" />
          </ul>
          {(rr.unmet || []).length > 0 && (
            <p className="mt-1 text-[11px] text-red-600" data-testid="escrow-release-unmet">Blocked by: {rr.unmet.join(", ")}</p>
          )}
        </div>
      </div>

      {holds.length > 0 && (
        <div data-testid="escrow-holds">
          <div className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Active holds</div>
          <ul className="space-y-1">
            {holds.map((h, i) => (
              <li key={i} className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-1.5 text-sm text-red-700">
                <AlertTriangle size={13} /> {h.type}: {h.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data?.events?.length > 0 && (
        <div data-testid="escrow-timeline">
          <div className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Funds status timeline</div>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-slate-500">
            {data.events.map((e, i) => (
              <li key={i} className="flex items-center gap-1.5"><Clock size={11} /> {new Date(e.created_at).toLocaleString()} — {e.event || e.kind || e.status}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[11px] text-slate-400">Escrow actions are governed entirely by the backend state machine — this screen is read-only.</p>
    </div>
  );
}
